<template>
  <Toolbar class="mb-4 mt-0 p-2 toolbar">
    <template #start>
      <Button
        v-tooltip="backTooltipConfig"
        data-testid="detail-back"
        icon="pi pi-chevron-left"
        variant="link"
        :autofocus="true"
        @click="$emit('goToList')"
      />
      <slot name="headerText">
        <span v-if="headerText" class="font-bold">
          {{ t('text.detailToolbarHeader', { name: headerText?.toUpperCase() }) }}
        </span>
        <span v-else class="font-bold">
          {{ t('text.detailToolbarHeaderDefault') }}
        </span>
      </slot>
      <div id="toolbar-start-portal"></div>
    </template>
    <template #center>
      <slot name="center"></slot>
      <div id="toolbar-center-portal"></div>
    </template>
    <template #end>
      <slot name="end">
        <!-- Default buttons fallback -->
        <ButtonGroup v-if="showEditButton || showDeleteButton" class="bg-primary">
          <Button
            v-if="!isNew && editMode && showDeleteButton"
            icon="pi pi-trash"
            :label="$t('buttons.delete')"
            severity="danger"
            :disabled="!profileStore.userProfile?.isSeyboldUser"
            @click="confirmDelete"
          />
          <Button
            v-if="showToggleEditModeButton && showEditButton"
            :icon="editMode ? 'pi pi-eye' : 'pi pi-pencil'"
            :disabled="!profileStore.userProfile?.isSeyboldUser"
            @click="$emit('toggleEditMode')"
          />
        </ButtonGroup>
      </slot>
      <div id="toolbar-end-portal"></div>
    </template>
  </Toolbar>
  <Confirm ref="confirmDialog" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Confirm from '@/components/confirm/Confirm.vue';
import { useProfileStore } from '@/stores/profile';
import { useReturnToStore } from '@/stores/returnTo';

import type { DetailToolbarEmits, DetailToolbarProps } from './detailToolbarTypes';

const props = withDefaults(defineProps<DetailToolbarProps>(), {
  showDeleteButton: true,
  showEditButton: true,
});

const emit = defineEmits<DetailToolbarEmits>();
const returnToStore = useReturnToStore();
const route = useRoute();
const profileStore = useProfileStore();

// i18n
const { t } = useI18n();

// Confirm Dialog
const confirmDialog = ref<InstanceType<typeof Confirm> | null>(null);

const showToggleEditModeButton = computed(() => {
  return !props.isNew && (!props.editMode || (props.editMode && !props.isAnyFieldDirty));
});

// Confirm Delete
async function confirmDelete() {
  if (!confirmDialog.value) return;

  const isConfirmed = await confirmDialog.value.isDeleteConfirmed(props.confirmationMessage);

  if (isConfirmed) {
    emit('confirmDelete');
  }
}

const hasReturnTo = computed(() => !!returnToStore.peek());

const isActionRoute = computed(() => {
  if (route.path.includes('/action/')) {
    return true;
  }

  const matched = route.matched;
  if (!matched.length) {
    return false;
  }

  // Detect dynamic action context from route records.
  return matched.some((record) => String(record.path).includes('/action/'));
});

const backTooltipText = computed(() => {
  // Action flows should use a specific return label.
  if (isActionRoute.value) {
    return t('titletags.back2ticket'); // can be less specific in the future if action route is not only used on tickets
  }

  return hasReturnTo.value ? t('titletags.back2origin') : t('titletags.back2list');
});

const backTooltipConfig = computed(() => ({
  value: backTooltipText.value,
  showDelay: 300,
}));
</script>

<style scoped lang="css">
@layer app {
  /* :deep() */
  .pi-chevron-left.p-button-icon {
    font-size: 0.6em;
  }
}
</style>
